import React, { useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Upload, FileUp, CheckCircle, AlertTriangle, Loader2, HardDrive, Fuel } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function BinUpload({ onUploadComplete }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const uploadFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.bin')) {
      setError('Invalid file type. Please upload a .bin file.');
      return;
    }
    if (file.size !== 2097152) {
      setError(`Invalid file size. ECU binaries must be exactly 2MB (2,097,152 bytes). Your file is ${(file.size / 1024 / 1024).toFixed(2)} MB.`);
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
      const formData = new FormData();
      formData.append('file', file);

      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_URL}/api/bin/upload`, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await res.json();
      setResult(data);
      if (onUploadComplete) onUploadComplete(data);
    } catch (err) {
      setError(err.message);
    }
    setUploading(false);
  }, [onUploadComplete]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);
  const handleFileSelect = (e) => { if (e.target.files?.[0]) uploadFile(e.target.files[0]); };

  return (
    <Card className="bg-[#111111] border-[#27272A] rounded-sm" data-testid="bin-upload">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit'] text-base">
          <HardDrive className="w-5 h-5 text-[#FF5E1A]" />Upload ECU Binary
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Drop Zone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => inputRef.current?.click()}
          data-testid="bin-dropzone"
          className={`relative border-2 border-dashed rounded-sm p-10 text-center cursor-pointer transition-all ${
            dragging
              ? 'border-[#FF5E1A] bg-[#FF5E1A]/5'
              : 'border-[#27272A] hover:border-zinc-600 bg-[#0A0A0A]'
          }`}
        >
          <input ref={inputRef} type="file" accept=".bin" onChange={handleFileSelect} className="hidden" />
          
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-[#FF5E1A] animate-spin" />
              <p className="text-sm text-zinc-300">Analyzing ECU binary...</p>
            </div>
          ) : (
            <>
              <FileUp className={`w-10 h-10 mx-auto mb-3 ${dragging ? 'text-[#FF5E1A]' : 'text-zinc-600'}`} />
              <p className="text-sm text-zinc-300 mb-1">
                {dragging ? 'Drop your .bin file here' : 'Drag & drop your ECU binary here'}
              </p>
              <p className="text-xs text-zinc-600">or click to browse — .bin files only, exactly 2MB</p>
            </>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mt-4 flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-sm" data-testid="bin-upload-error">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-400">{error}</p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="mt-4 p-4 bg-[#0A0A0A] border border-emerald-500/20 rounded-sm" data-testid="bin-upload-result">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-bold text-white font-['Outfit']">{result.filename}</span>
              <Badge className="bg-[#FF5E1A]/10 text-[#FF5E1A] text-[10px] px-1.5 py-0 rounded-sm border border-[#FF5E1A]/20">
                <Fuel className="w-3 h-3 mr-1" />{result.fuel_type}
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <p className="text-zinc-500">Size</p>
                <p className="text-white font-['JetBrains_Mono']">{(result.size_bytes / 1024).toFixed(0)} KB</p>
              </div>
              <div>
                <p className="text-zinc-500">Fuel Type</p>
                <p className="text-white font-['JetBrains_Mono']">{result.fuel_type}</p>
              </div>
              <div>
                <p className="text-zinc-500">Water Pump</p>
                <p className={`font-['JetBrains_Mono'] ${result.analysis?.safety?.water_pump_safe ? 'text-emerald-400' : 'text-zinc-400'}`}>
                  {result.analysis?.safety?.water_pump_safe === true ? 'SAFE' : 'Unknown'}
                </p>
              </div>
              <div>
                <p className="text-zinc-500">Valid Size</p>
                <p className={`font-['JetBrains_Mono'] ${result.analysis?.safety?.file_size_valid ? 'text-emerald-400' : 'text-red-400'}`}>
                  {result.analysis?.safety?.file_size_valid ? 'YES' : 'NO'}
                </p>
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-3">Binary uploaded and analyzed. View maps in the Interactive Map Viewer tab below.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
